import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Trophy, Radio, Circle, Clock, CheckCircle2 } from "lucide-react";
import { fileUrl } from "@/components/ImageUpload";
import SponsorTicker from "@/components/SponsorTicker";
import AdShowcase from "@/components/AdShowcase";
import MatchClock from "@/components/MatchClock";
import { shortTeam } from "@/lib/tournament";
import api from "@/lib/api";

function ServeDots({ n, size = "w-3 h-3" }) {
  const count = n === 2 ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`serve-dots-${count}`} title={`${count === 2 ? "2nd" : "1st"} serve`}>
      {Array.from({ length: count }).map((_, i) => <Circle key={i} className={`${size} fill-current text-primary-c`} />)}
    </span>
  );
}

function BigMatch({ m, finished }) {
  const aWin = finished ? m.winner_name === m.a_name : m.a > m.b;
  const bWin = finished ? m.winner_name === m.b_name : m.b > m.a;
  return (
    <div className="rounded-3xl glow-border p-6 relative" style={{ background: "var(--surface)", boxShadow: finished ? "0 0 30px rgba(34,197,94,0.18)" : "none" }} data-testid={finished ? `tv-final-${m.match_id}` : `tv-live-${m.match_id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs mono uppercase tracking-[0.2em]" style={{ color: "var(--text-2)" }} data-testid={`tv-round-${m.match_id}`}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
        {finished
          ? <span className="text-[10px] mono uppercase tracking-[0.2em] flex items-center gap-1.5" style={{ color: "#22c55e" }}><CheckCircle2 className="w-3.5 h-3.5" /> Final</span>
          : <MatchClock startedAt={m.started_at} className="text-xs text-primary-c" />}
      </div>
      <div className="flex items-center justify-between mb-3">
        <span className="font-display font-bold text-2xl flex items-center gap-2 truncate">{!finished && m.server === "a" && <ServeDots n={m.server_number} />}{shortTeam(m.a_name)}</span>
        <span className="font-display font-black text-6xl mono ml-4" style={{ color: aWin ? "#22c55e" : "var(--text)" }}>{m.a}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-display font-bold text-2xl flex items-center gap-2 truncate">{!finished && m.server === "b" && <ServeDots n={m.server_number} />}{shortTeam(m.b_name)}</span>
        <span className="font-display font-black text-6xl mono ml-4" style={{ color: bWin ? "#22c55e" : "var(--text)" }}>{m.b}</span>
      </div>
      {finished && m.winner_name && (
        <div className="mt-4 pt-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }} data-testid={`tv-winner-${m.match_id}`}>
          <Trophy className="w-5 h-5" style={{ color: "#eab308" }} />
          <span className="font-display font-black text-xl">{m.winner_name}</span>
          <span className="text-sm" style={{ color: "var(--text-2)" }}>wins</span>
        </div>
      )}
    </div>
  );
}

function ResultCard({ m }) {
  return (
    <div className="rounded-2xl glow-border px-4 py-3" style={{ background: "var(--surface)" }} data-testid={`tv-result-${m.match_id}`}>
      <div className="text-[10px] mono uppercase tracking-wider mb-1" style={{ color: "var(--text-2)" }}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm truncate ${m.winner_name === m.a_name ? "font-bold" : ""}`} style={{ color: m.winner_name === m.a_name ? "#22c55e" : "var(--text)" }}>{shortTeam(m.a_name)}</span>
        <span className="mono font-black">{m.a}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm truncate ${m.winner_name === m.b_name ? "font-bold" : ""}`} style={{ color: m.winner_name === m.b_name ? "#22c55e" : "var(--text)" }}>{shortTeam(m.b_name)}</span>
        <span className="mono font-black">{m.b}</span>
      </div>
    </div>
  );
}

export default function Scoreboard() {
  const { tid } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/public/tournaments/${tid}/live`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [tid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-8 h-8 animate-spin text-primary-c" /></div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "var(--bg)", color: "var(--text)" }}><div><Trophy className="w-12 h-12 mx-auto text-primary-c opacity-60" /><p className="mt-4 text-lg" style={{ color: "var(--text-2)" }}>Tournament not live.</p></div></div>;

  const t = data.tournament;
  const spectatorUrl = `${window.location.origin}/spectator/${tid}`;
  const now = Date.now();
  const liveIds = new Set(data.live_matches.map((m) => m.match_id));
  const lingering = (data.recent_results || []).filter((r) => !liveIds.has(r.match_id) && r.ended_at && (now - new Date(r.ended_at).getTime()) < 25000);
  const results = data.recent_results || [];

  return (
    <div className="min-h-screen flex flex-col cyber-grid" style={{ background: "var(--bg)", color: "var(--text)" }} data-testid="scoreboard">
      <header className="flex items-center justify-between gap-6 px-8 py-5 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-4 min-w-0">
          {t.banner_url && <img src={fileUrl(t.banner_url)} alt="" className="h-14 w-24 object-cover rounded-xl glow-border" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /><span className="text-xs mono uppercase tracking-[0.3em] text-primary-c">Live</span></div>
            <h1 className="font-display font-black text-3xl tracking-tight truncate" data-testid="scoreboard-name">{t.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-testid="scoreboard-qr">
          <div className="text-right"><div className="text-xs mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Follow live</div><div className="text-[10px]" style={{ color: "var(--text-2)" }}>scan to watch on your phone</div></div>
          <div className="bg-white p-2 rounded-xl"><QRCodeCanvas value={spectatorUrl} size={72} /></div>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 px-8 py-6 grain overflow-y-auto">
        <div className="flex-1 min-w-0">
        <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-primary-c" /> On court now</h2>
        {data.live_matches.length === 0 && lingering.length === 0 ? (
          <div className="rounded-3xl glow-border p-12 text-center" style={{ background: "var(--surface)" }}>
            <p className="text-lg" style={{ color: "var(--text-2)" }}>No matches in progress. Next matches are queued below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="scoreboard-live">
            {data.live_matches.map((m) => <BigMatch key={m.match_id} m={m} />)}
            {lingering.map((m) => <BigMatch key={m.match_id} m={m} finished />)}
          </div>
        )}

        {data.upcoming_matches.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-primary-c" /> Coming up</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="scoreboard-upcoming">
              {data.upcoming_matches.slice(0, 9).map((m) => (
                <div key={m.match_id} className="rounded-2xl glow-border px-5 py-3" style={{ background: "var(--surface)" }} data-testid={`tv-upcoming-${m.match_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
                    {m.court_name && <div className="text-[10px] mono uppercase text-primary-c">{m.court_name}</div>}
                  </div>
                  <div className="font-bold mt-1">{m.a_name} <span className="text-primary-c">vs</span> {shortTeam(m.b_name)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><Trophy className="w-4 h-4" style={{ color: "#eab308" }} /> Recent results</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="scoreboard-results">
              {results.slice(0, 9).map((m) => <ResultCard key={m.match_id} m={m} />)}
            </div>
          </div>
        )}
        </div>
        <AdShowcase />
      </div>

      <SponsorTicker />
    </div>
  );
}
